<template>
  <div class="main-mobile-header">
    <div class="main-mobile-header__left">
      <RouterLink
        :to="{ name: ROUTE_NAME.DASHBOARDS }"
        class="main-mobile-header__logo main-mobile-header-button"
      >
        <AppLogo />
      </RouterLink>
      <div v-if="isRefreshButtonVisible" class="main-mobile-header__refresh">
        <UIButtonIcon
          :leading="ArrowPathRoundedSquareIcon"
          variant="secondary"
          fill="light"
          @click="handleRefresh"
        />
      </div>
    </div>
    <div class="main-mobile-header__page">
      <PageName />
    </div>
    <div class="main-mobile-header__actions">
      <HeaderPowerMode />
      <HeaderNotifications />
      <HeaderUser />
    </div>
  </div>
  <AppDisruptionMessage v-if="isDisruptionMessageVisible" />
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'

import { ROUTE_NAME, IS_DISRUPTION_MESSAGE_VISIBLE } from '@/const'

import useAnalyticsStore from '@/store/analytics'

import HeaderNotifications from './HeaderNotifications.vue'
import HeaderUser from './HeaderUser.vue'
import HeaderPowerMode from '../../components/HeaderPowerMode.vue'
import PageName from '../../components/PageName.vue'

import { AppLogo, AppDisruptionMessage } from '@app'
import { UIButtonIcon } from '@ui/buttons'
import { ArrowPathRoundedSquareIcon } from '@heroicons/vue/24/outline'

type Props = {
  disabled?: boolean
}

defineProps<Props>()

const analyticsStore = useAnalyticsStore()

const isDisruptionMessageVisible = inject<boolean>(IS_DISRUPTION_MESSAGE_VISIBLE, false)

const isRefreshButtonVisible = computed(() => analyticsStore.isWaitingToRefresh)

const handleRefresh = () => {
  analyticsStore.markAsDeprecated()
}
</script>

<style scoped>
.main-mobile-header {
  @apply absolute top-0 inset-x-0;
  @apply flex items-center justify-between shrink-0 gap-4;
  @apply px-2 sm:px-4 pt-safe-offset-2 pb-2;
  @apply bg-primary;

  &__left {
    @apply absolute;
    @apply flex items-center gap-1;
  }

  &__logo {
    @apply text-white;
  }

  :deep() .logo__symbol {
    @apply w-auto h-auto;
  }

  &__area {
    @apply flex items-center;
  }

  &__page {
    @apply flex flex-auto justify-center gap-3 sm:gap-4;
    @apply px-16;
    @apply text-white;
  }

  &__refresh {
    :deep(.ui-button) {
      @apply hover:bg-transparent;
      @apply hover:text-indigo-300;
    }
  }

  &__actions {
    @apply absolute right-2 sm:right-6;
    @apply flex items-center;
    @apply gap-2;
  }
}
</style>

<style>
.main-mobile-header-button {
  @apply h-full;
  @apply aspect-square;
  @apply flex items-center justify-center;
  @apply cursor-pointer;
}
.main-mobile-header-icon {
  @apply w-6 h-6 md:w-7 md:h-7;
  @apply text-indigo-200;
}
</style>
